import React, { useState } from 'react';
import { Eye, EyeOff, X, Check } from 'lucide-react';
import { PartnerAuthService } from '../../../lib/services/partnerAuthService';
import { MinimalLoader } from '../../ui/MinimalLoader';
import type { ExternalPartner, PartnerPortalAccess } from '../../../lib/types';

// Adattamento da PartnerPortalAccessManager.tsx (campi, validazioni e service call
// invariati: createPartnerAccess / updatePartnerPermissions / resetPartnerPassword /
// disablePartnerAccess). Per integrarsi nel container Notion (stesso schema di
// EditAccessForm nel Task 2, che riceve `access` come prop invece di leggerlo da sé):
// - `access` arriva già caricato dal container (che lo usa anche per il sunto e i
//   PropRow), niente più fetch/loading interno.
// - Il banner "credenziali generate" e il blocco "Info Accesso Corrente" sono stati
//   tolti da qui: quei dati vivono ora nella vista Notion (PartnerPortalAccessDetails +
//   DrawerCallout, identico al Task 2), e la password appena creata/cambiata viene
//   passata in su con `onSaved` invece di essere renderizzata due volte.
// - I due messaggi di errore erano in inglese nell'originale ("Passwords do not match",
//   "Password must be at least 8 characters long"): tradotti, come richiesto dal mockup
//   approvato ("Correzioni di sostanza da approvare insieme al disegno").
export interface PartnerPortalFormProps {
  partner: ExternalPartner;
  access: PartnerPortalAccess | null;
  onClose(): void;
  onSaved(password: string | null): void;
}

export function PartnerPortalForm({ partner, access, onClose, onSaved }: PartnerPortalFormProps): JSX.Element {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: access?.username || `${partner.name.toLowerCase().replace(/\s+/g, '.')}.partner`,
    password: '',
    confirmPassword: '',
    is_active: access?.is_active ?? true,
    permissions: access?.permissions ?? ['view_services', 'request_quotes']
  });

  const handleSubmit = async () => {
    if (!access && formData.password !== formData.confirmPassword) {
      setError('Le password non coincidono');
      return;
    }

    if (!access && formData.password.length < 8) {
      setError('La password deve avere almeno 8 caratteri');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (access) {
        await PartnerAuthService.updatePartnerPermissions(access.id, formData.permissions);

        const passwordChanged = !!formData.password && formData.password === formData.confirmPassword;
        if (passwordChanged) {
          await PartnerAuthService.resetPartnerPassword(access.id, formData.password);
        }

        if (formData.is_active !== access.is_active) {
          if (formData.is_active) {
            await PartnerAuthService.enablePartnerAccess(access.id);
          } else {
            await PartnerAuthService.disablePartnerAccess(access.id);
          }
        }

        onSaved(passwordChanged ? formData.password : null);
      } else {
        const newAccess = await PartnerAuthService.createPartnerAccess({
          partner_id: partner.id,
          username: formData.username,
          password: formData.password,
          permissions: formData.permissions,
          is_active: formData.is_active
        });

        if (newAccess) {
          onSaved(formData.password);
        } else {
          throw new Error('Creazione accesso non riuscita. Username già in uso?');
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Salvataggio non riuscito');
    } finally {
      setSaving(false);
    }
  };

  const handlePermissionChange = (permission: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      permissions: checked
        ? [...prev.permissions, permission]
        : prev.permissions.filter((p) => p !== permission)
    }));
  };

  return (
    <div className="border border-blue-200 dark:border-blue-800 rounded-lg bg-blue-50/50 dark:bg-blue-900/10 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-black dark:text-white">
          {access ? `Modifica accesso: ${access.username}` : 'Crea accesso portale partner'}
        </h4>
        <button onClick={onClose} className="p-1 hover:bg-gray-200 dark:hover:bg-white/10 rounded">
          <X className="w-4 h-4" />
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}

      <div>
        <label className="block text-xs font-medium text-black/60 dark:text-white/60 mb-1">Username</label>
        <input
          type="text"
          required
          value={formData.username}
          onChange={(e) => setFormData((prev) => ({ ...prev, username: e.target.value }))}
          disabled={!!access}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm font-mono disabled:bg-gray-100 dark:disabled:bg-white/5"
        />
        {access && (
          <p className="text-xs text-black/60 dark:text-white/60 mt-1">Username non può essere modificato dopo la creazione</p>
        )}
      </div>

      <div>
        <label className="block text-xs font-medium text-black/60 dark:text-white/60 mb-1">
          {access ? 'Nuova password (lascia vuoto per mantenerla)' : 'Password'}
        </label>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            required={!access}
            value={formData.password}
            onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
            minLength={8}
            className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-black/40 dark:text-white/40 hover:text-black/70 dark:hover:text-white/70"
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {(!access || formData.password) && (
        <div>
          <label className="block text-xs font-medium text-black/60 dark:text-white/60 mb-1">Conferma password</label>
          <input
            type="password"
            required
            value={formData.confirmPassword}
            onChange={(e) => setFormData((prev) => ({ ...prev, confirmPassword: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm"
          />
        </div>
      )}

      <div>
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={formData.is_active}
            onChange={(e) => setFormData((prev) => ({ ...prev, is_active: e.target.checked }))}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="ml-2 text-sm text-black dark:text-white">Account attivo</span>
        </label>
        <p className="text-xs text-black/60 dark:text-white/60 mt-1">
          Gli account inattivi non possono accedere al portale partner
        </p>
      </div>

      <div>
        <label className="block text-xs font-medium text-black/60 dark:text-white/60 mb-2">Permessi</label>
        <div className="space-y-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.permissions.includes('view_services')}
              onChange={(e) => handlePermissionChange('view_services', e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <div className="ml-3">
              <div className="text-sm text-black dark:text-white">Visualizza servizi</div>
              <div className="text-xs text-black/60 dark:text-white/60">Accesso al catalogo servizi</div>
            </div>
          </label>

          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.permissions.includes('request_quotes')}
              onChange={(e) => handlePermissionChange('request_quotes', e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <div className="ml-3">
              <div className="text-sm text-black dark:text-white">Richiedi preventivi</div>
              <div className="text-xs text-black/60 dark:text-white/60">Possibilità di inviare richieste di preventivo</div>
            </div>
          </label>

          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.permissions.includes('view_reports')}
              onChange={(e) => handlePermissionChange('view_reports', e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <div className="ml-3">
              <div className="text-sm text-black dark:text-white">Visualizza report</div>
              <div className="text-xs text-black/60 dark:text-white/60">Accesso ai report di performance del partner</div>
            </div>
          </label>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
        >
          Annulla
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={saving}
          className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-colors flex items-center gap-2"
        >
          {saving ? <MinimalLoader size="sm" /> : <Check className="w-4 h-4" />}
          {access ? 'Salva modifiche' : 'Crea accesso'}
        </button>
      </div>
    </div>
  );
}
